import * as React from 'react'

import { PopoverStateReturn } from '../popover'
import { SelectItem } from './types'

function getSelectedItemIndex(items: SelectItem<unknown>[], value: unknown) {
  if (!value) return -1

  return items.findIndex((item) => item.value === value)
}

/**
 * Hook which handles select state and returns onChange handler
 */
export function useSelect<ValueType, ItemType extends SelectItem<ValueType>>(
  state: PopoverStateReturn,
  defaultValue: ValueType | undefined,
  items: ItemType[],
  onChange: (item: ItemType) => void
) {
  const [selectedIndex, setSelectedIndex] = React.useState(getSelectedItemIndex(items, defaultValue))
  const closePopover = React.useRef(() => state.hide())

  // Update select value on change from outside
  React.useEffect(() => {
    const newIndex = getSelectedItemIndex(items, defaultValue)
    setSelectedIndex(newIndex)
  }, [items, selectedIndex, defaultValue])

  const onItemChange = React.useCallback(
    (index: number, closeOnSelect = true) => {
      const selectedItem = items[index]
      setSelectedIndex(index)

      if (!selectedItem.href || !selectedItem.reloadDocument) {
        onChange(selectedItem)
        if (closeOnSelect) closePopover.current()
        return
      }

      if (typeof selectedItem.href === 'string') {
        window.location.href = selectedItem.href
        return
      }

      const newUrl = new URL(selectedItem.href.pathname!, window.location.href)
      newUrl.search = selectedItem.href.search ?? ''
      newUrl.hash = selectedItem.href.hash ?? ''

      window.location.href = newUrl.toString()
    },
    [items, onChange, setSelectedIndex]
  )

  return { selectedIndex, onItemChange }
}
